import { centroid } from '@turf/turf';
import { MultiPolygon, Polygon, Position } from 'geojson';

import { Coordinates } from '@/types/commons/commons.types';

export function getFirstPolygon(geometry: Polygon): Polygon;
export function getFirstPolygon(geometry: MultiPolygon): Polygon;
export function getFirstPolygon(geometry: Polygon | MultiPolygon | undefined): Polygon | undefined;

export function getFirstPolygon(geometry: Polygon | MultiPolygon | undefined): Polygon | undefined {
  if (!geometry) {
    return undefined;
  }
  if (geometry.type === 'Polygon') {
    return geometry;
  }
  return {
    type: 'Polygon',
    coordinates: geometry.coordinates[0],
  };
}

export function getEditableRing(geometry: Polygon | MultiPolygon, polygonIndex = 0, ringIndex = 0): Position[] {
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;

  if (polygonIndex >= polygons.length) {
    throw new Error(`Invalid polygonIndex ${polygonIndex}`);
  }

  const rings = polygons[polygonIndex];

  if (ringIndex >= rings.length) {
    throw new Error(`Invalid ringIndex ${ringIndex}`);
  }

  return rings[ringIndex];
}

export function updateCoordinate(
  geometry: Polygon | MultiPolygon,
  pointIndex: number,
  coordIndex: number,
  value: number,
  polygonIndex = 0,
  ringIndex = 0,
): Polygon | MultiPolygon {
  const next = structuredClone(geometry);
  const ring = getEditableRing(next, polygonIndex, ringIndex);

  ring[pointIndex][coordIndex] = value;

  return next;
}

export function deleteCoordinate(
  geometry: Polygon | MultiPolygon,
  pointIndex: number,
  polygonIndex = 0,
  ringIndex = 0,
): Polygon | MultiPolygon {
  const next = structuredClone(geometry);
  const ring = getEditableRing(next, polygonIndex, ringIndex);

  if (ring.length <= 4) return next;

  ring.splice(pointIndex, 1);
  ring[ring.length - 1] = [...ring[0]];

  return next;
}

export function addCoordinate(
  geometry: Polygon | MultiPolygon,
  polygonIndex = 0,
  ringIndex = 0,
): Polygon | MultiPolygon {
  const next = structuredClone(geometry);
  const ring = getEditableRing(next, polygonIndex, ringIndex);

  const lastRealPoint = ring.at(-2);
  if (!lastRealPoint) return next;

  const newPoint: [number, number] = [lastRealPoint[0] + 0.01, lastRealPoint[1] + 0.01];
  ring.splice(-1, 0, newPoint);
  ring[ring.length - 1] = [...ring[0]];

  return next;
}

export function getCentroid(polygon: Polygon): Coordinates {
  const centroidPosition = centroid(polygon).geometry.coordinates;
  return {
    longitude: centroidPosition[0],
    latitude: centroidPosition[1],
    type: '2D',
  };
}
